import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, EyeOff, ImageOff, FileUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, imgUrl, errMsg } from "@/lib/api";
import { euro, fmtWeight } from "@/lib/shop";
import { ProductBadges } from "@/components/Badges";
import { Spinner } from "@/components/ProtectedRoute";
import { ProductFormDialog } from "./ProductForm";
import { ImportCsvDialog } from "./ImportCsvDialog";

const Row = ({ p, onEdit, onDelete }) => (
  <div data-testid={`admin-product-row-${p.id}`} className="card-mc flex items-center gap-3 p-3">
    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-border bg-white">
      {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="h-full w-full object-contain" /> : <div className="grid h-full place-items-center text-muted-foreground"><ImageOff className="h-5 w-5" /></div>}
    </div>
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-bold">{p.name}</div>
      <ProductBadges p={p} className="mt-1" />
      <div className="mt-1 text-xs text-muted-foreground">
        {euro(p.price)} · Qtà <b data-testid={`admin-product-qty-${p.id}`}>{p.quantity}</b> · {fmtWeight(p.weight_g)}
        {p.quantity === 0 && <span className="ml-2 inline-flex items-center gap-1 font-bold text-red-600"><EyeOff className="h-3 w-3" /> Non visibile</span>}
      </div>
    </div>
    <button data-testid={`admin-edit-product-${p.id}`} aria-label="Modifica" className="btn-ghost-mc !p-2" onClick={() => onEdit(p)}><Pencil className="h-4 w-4" /></button>
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`admin-delete-product-${p.id}`} aria-label="Elimina" className="btn-ghost-mc !p-2"><Trash2 className="h-4 w-4" /></button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminare l'articolo?</AlertDialogTitle>
          <AlertDialogDescription>"{p.name}" verrà rimosso dal negozio. Gli ordini già fatti restano salvati.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Annulla</AlertDialogCancel>
          <AlertDialogAction data-testid={`admin-confirm-delete-${p.id}`} onClick={() => onDelete(p)}>Elimina</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
);

export default function AdminProducts() {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get("/admin/products").then(({ data }) => setProducts(data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Articolo eliminato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/inventory/sync");
      toast.success(`Inventario sincronizzato: ${data.sent} articoli inviati al foglio`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  if (!products) return <Spinner />;
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{products.length} articoli · quantità 0 = non visibile ai clienti</p>
        <div className="flex flex-wrap justify-end gap-2">
          <button data-testid="admin-inventory-sync-button" className="btn-ghost-mc" onClick={sync} disabled={syncing}>
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sincronizzo…" : "Sincronizza inventario"}
          </button>
          <button data-testid="admin-import-csv-button" className="btn-ghost-mc" onClick={() => setImporting(true)}>
            <FileUp className="h-4 w-4" /> Importa da CSV
          </button>
          <button data-testid="admin-add-product-button" className="btn-mc" onClick={() => setEditing({})}>
            <Plus className="h-4 w-4" /> Aggiungi articolo
          </button>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {products.map((p) => <Row key={p.id} p={p} onEdit={setEditing} onDelete={del} />)}
        {products.length === 0 && <div data-testid="admin-products-empty" className="card-mc p-8 text-center text-sm text-muted-foreground">Nessun articolo. Aggiungi il primo!</div>}
      </div>
      {editing && <ProductFormDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {importing && <ImportCsvDialog onClose={() => setImporting(false)} onImported={load} />}
    </div>
  );
}
